/* Shared filter bar for the PCIS Health pages (Summary + Details): a date range,
   courier + account scoping (account options narrow to the selected courier), and
   an optional "Only failed" toggle (Details only). Courier/account options come
   from the same courier-accounts list used by the Manage page.

   Date range uses the shared DateRangePicker (day granularity). `from`/`to` are
   sent to the API as `yyyy-MM-ddTHH:mm:ss` — from = start of the first day, to =
   end of the last. Fields edit a local DRAFT; nothing hits the query until
   "Filter" is clicked ("Reset" restores today's range). Default window = today. */
import React, {useMemo, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Typography, Switch, Button} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import {format, startOfDay, endOfDay, isValid} from 'date-fns'
import DateRangePicker from '../../../components/DateRangePicker'
import {SearchSelect} from '../components'

const API_FMT = "yyyy-MM-dd'T'HH:mm:ss"
const toApi = (d) => (d && isValid(d) ? format(d, API_FMT) : '')
const parse = (s) => {
  if (!s) return null
  const d = new Date(s)
  return isValid(d) ? d : null
}

/* today's window (start-of-day → end-of-day) — the default filter */
export const todayRange = () => {
  const n = new Date()
  return {from: toApi(startOfDay(n)), to: toApi(endOfDay(n))}
}
/* full default filter set used to seed pages and on Reset */
export const defaultFilters = (extra = {}) => ({...todayRange(), courier: '', account: '', ...extra})

const FIELD_H = 38
const FIELD_W = 240

function FilterField({label, sx, children}) {
  return (
    <Box sx={sx}>
      <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5}}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}
FilterField.propTypes = {label: PropTypes.node, sx: PropTypes.object, children: PropTypes.node}

/* the shared DateRangePicker's TextField, flattened to match the SearchSelects */
const dateFieldSx = {
  '& .MuiOutlinedInput-root': {height: FIELD_H, borderRadius: '6px', fontSize: 13, background: '#fff', py: 0, pr: '4px'},
  '& .MuiOutlinedInput-input': {fontSize: 13, py: 0, height: '100%', display: 'flex', alignItems: 'center'},
  '& .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--border, #E5E7EB)'},
  '& .MuiOutlinedInput-notchedOutline legend': {display: 'none'},
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--primary, #6366F1)', borderWidth: 1},
  '& .MuiInputLabel-root': {display: 'none'},
  '& .MuiInputAdornment-root': {height: 'auto', margin: 0},
  '& .MuiIconButton-root': {padding: '4px', color: 'var(--neutral, #637381)'},
  '& .MuiSvgIcon-root': {fontSize: 18},
}
const selectSx = {
  '& .MuiOutlinedInput-root': {height: FIELD_H, py: 0},
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {py: 0},
}

export default function PcisHealthFilterBar({filters, onChange, accounts, showOnlyFailed}) {
  const [draft, setDraft] = useState(filters)
  useEffect(() => setDraft(filters), [filters])
  const setD = (patch) => setDraft((d) => ({...d, ...patch}))

  const courierOptions = useMemo(() => {
    const codes = Array.from(new Set((accounts || []).map((a) => a.courier_code).filter(Boolean)))
    return codes.map((c) => ({value: c, label: c}))
  }, [accounts])

  const accountOptions = useMemo(() => {
    const scoped = draft.courier ? (accounts || []).filter((a) => a.courier_code === draft.courier) : accounts || []
    return scoped.map((a) => ({value: a.account_code, label: a.display_name || a.account_code}))
  }, [accounts, draft.courier])

  const rangeStart = parse(draft.from)
  const rangeEnd = parse(draft.to)
  const defaultRange = rangeStart && rangeEnd ? [{startDate: rangeStart, endDate: rangeEnd, key: 'selection'}] : []

  const onRangeChange = (range) => {
    const sel = range && range[0]
    if (!sel) return
    setD({from: toApi(startOfDay(sel.startDate)), to: toApi(endOfDay(sel.endDate))})
  }

  const apply = () => onChange(draft)
  const reset = () => {
    const next = defaultFilters({onlyFailed: false})
    setDraft(next)
    onChange(next)
  }

  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: '14px', bgcolor: 'background.paper', p: 2, mb: 2.5}}>
      <Stack direction='row' flexWrap='wrap' gap={1.5} alignItems='flex-end'>
        <FilterField label='Date range' sx={{flex: `0 0 ${FIELD_W}px`, ...dateFieldSx}}>
          <DateRangePicker showTextBox size='small' label='' placeholder='Select date range' maxDate={new Date()} defaultRange={defaultRange} onChange={onRangeChange} />
        </FilterField>
        <FilterField label='Courier' sx={{flex: `0 0 ${FIELD_W}px`}}>
          <SearchSelect
            placeholder='All couriers'
            value={draft.courier || ''}
            onChange={(v) => setD({courier: v, account: ''})}
            options={courierOptions}
            sx={selectSx}
          />
        </FilterField>
        <FilterField label='Account' sx={{flex: `0 0 ${FIELD_W}px`}}>
          <SearchSelect
            placeholder='All accounts'
            value={draft.account || ''}
            onChange={(v) => setD({account: v})}
            options={accountOptions}
            sx={selectSx}
          />
        </FilterField>
        {showOnlyFailed ? (
          <FilterField label='Only failed' sx={{flex: '0 0 140px'}}>
            <Box sx={{height: FIELD_H, border: 1, borderColor: 'divider', borderRadius: '6px', bgcolor: '#fff', display: 'flex', alignItems: 'center', pl: 0.5, pr: 1}}>
              <Switch size='small' checked={!!draft.onlyFailed} onChange={(e) => setD({onlyFailed: e.target.checked})} />
              <Typography sx={{fontSize: 12.5, fontWeight: 600, color: draft.onlyFailed ? '#DC2626' : 'text.secondary'}}>
                {draft.onlyFailed ? 'Failed only' : 'All'}
              </Typography>
            </Box>
          </FilterField>
        ) : null}
        <Box sx={{flex: 1}} />
        <Stack direction='row' spacing={1} sx={{mb: 0.25}}>
          <Button variant='contained' size='small' startIcon={<FilterAltOutlinedIcon />} onClick={apply} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 700}}>
            Filter
          </Button>
          <Button variant='outlined' size='small' startIcon={<RestartAltOutlinedIcon />} onClick={reset} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
            Reset
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

PcisHealthFilterBar.propTypes = {
  filters: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
    courier: PropTypes.string,
    account: PropTypes.string,
    onlyFailed: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  accounts: PropTypes.array,
  showOnlyFailed: PropTypes.bool,
}
